import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Empty, List, Tag, Tooltip, Typography } from 'antd';
import { timeStringToMinutes, type ScheduleOverviewDayDetail } from '@ubus/shared';

const { Text } = Typography;

function snapHourFloor(minutes: number): number {
  return Math.floor(minutes / 60) * 60;
}

function snapHourCeil(minutes: number): number {
  return Math.ceil(minutes / 60) * 60;
}

function fmtHm(time: string): string {
  return time.slice(0, 5);
}

export interface OverviewDayOccupancyProps {
  detail: ScheduleOverviewDayDetail;
  /** Hide idle vehicles beyond this many (fold). */
  maxIdleShown?: number;
  selectedTripId?: string | null;
  onTripClick?: (tripId: string) => void;
}

/** Read-only vehicle × hour occupancy from published trips (no plan engine). */
export function OverviewDayOccupancy({
  detail,
  maxIdleShown = 4,
  selectedTripId,
  onTripClick,
}: OverviewDayOccupancyProps) {
  const { t } = useTranslation();

  const tripsByVehicle = useMemo(() => {
    const map = new Map<string, typeof detail.trips>();
    for (const trip of detail.trips) {
      const list = map.get(trip.vehicleId) ?? [];
      list.push(trip);
      map.set(trip.vehicleId, list);
    }
    return map;
  }, [detail.trips]);

  const rows = useMemo(() => {
    const busy = detail.vehicles.filter((v) => (tripsByVehicle.get(v.id)?.length ?? 0) > 0);
    const idle = detail.vehicles.filter((v) => (tripsByVehicle.get(v.id)?.length ?? 0) === 0);
    const shownIdle = idle.slice(0, maxIdleShown);
    const hiddenIdle = idle.length - shownIdle.length;
    return { busy, shownIdle, hiddenIdle };
  }, [detail.vehicles, maxIdleShown, tripsByVehicle]);

  const { rangeStart, rangeEnd, hours } = useMemo(() => {
    const times = detail.trips.flatMap((tr) => [
      timeStringToMinutes(tr.pickupTime.slice(0, 5)),
      timeStringToMinutes(tr.endTime.slice(0, 5)),
    ]);
    const has = times.length > 0;
    const rawMin = has ? Math.min(...times) : 8 * 60;
    const rawMax = has ? Math.max(...times) : 18 * 60;
    const start = Math.max(0, snapHourFloor(rawMin) - 60);
    const end = Math.min(24 * 60, snapHourCeil(rawMax) + 60);
    const hs: number[] = [];
    for (let h = start; h <= end; h += 60) hs.push(h);
    return { rangeStart: start, rangeEnd: end, hours: hs };
  }, [detail.trips]);

  const span = Math.max(rangeEnd - rangeStart, 60);

  if (detail.vehicles.length === 0 && detail.trips.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('scheduleOverview.noOccupancy')} />;
  }

  const renderRow = (vehicleId: string, fleetNumber: string) => {
    const trips = tripsByVehicle.get(vehicleId) ?? [];
    return (
      <div
        key={vehicleId}
        style={{
          display: 'grid',
          gridTemplateColumns: '88px 1fr',
          gap: 8,
          alignItems: 'center',
          minHeight: 36,
        }}
      >
        <Text strong style={{ fontSize: 12 }} ellipsis>
          {fleetNumber}
        </Text>
        <div
          style={{
            position: 'relative',
            height: 28,
            background: 'var(--ant-color-fill-quaternary, #f5f5f5)',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          {hours.map((h) => (
            <div
              key={h}
              style={{
                position: 'absolute',
                left: `${((h - rangeStart) / span) * 100}%`,
                top: 0,
                bottom: 0,
                width: 1,
                background: 'var(--ant-color-border-secondary, #e8e8e8)',
              }}
            />
          ))}
          {trips.map((tr) => {
            const start = timeStringToMinutes(tr.pickupTime.slice(0, 5));
            const end = Math.max(start + 15, timeStringToMinutes(tr.endTime.slice(0, 5)));
            const left = ((start - rangeStart) / span) * 100;
            const width = ((end - start) / span) * 100;
            const selected = selectedTripId === tr.id;
            return (
              <Tooltip
                key={tr.id}
                title={`${fmtHm(tr.pickupTime)}–${fmtHm(tr.endTime)} · ${tr.label} · ${tr.passengerCount}p · ${t('scheduleOverview.peekClickHint')}`}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onTripClick?.(tr.id);
                  }}
                  aria-label={t('scheduleOverview.peekOpenAria', { label: tr.label })}
                  style={{
                    position: 'absolute',
                    left: `${left}%`,
                    width: `${Math.max(width, 1.5)}%`,
                    top: 4,
                    bottom: 4,
                    background: selected ? '#69b1ff' : '#91caff',
                    border: selected ? '2px solid #0958d9' : '1px dashed #1677ff',
                    borderRadius: 3,
                    overflow: 'hidden',
                    padding: '0 4px',
                    fontSize: 10,
                    lineHeight: '18px',
                    whiteSpace: 'nowrap',
                    color: '#003eb3',
                    cursor: onTripClick ? 'pointer' : 'default',
                    textAlign: 'left',
                    zIndex: selected ? 2 : 1,
                  }}
                >
                  {fmtHm(tr.pickupTime)} {tr.label}
                </button>
              </Tooltip>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '88px 1fr',
          gap: 8,
          marginBottom: 4,
        }}
      >
        <span />
        <div style={{ position: 'relative', height: 16 }}>
          {hours.map((h) => (
            <Text
              key={h}
              type="secondary"
              style={{
                position: 'absolute',
                left: `${((h - rangeStart) / span) * 100}%`,
                fontSize: 10,
                transform: 'translateX(-50%)',
              }}
            >
              {minutesToHourLabel(h)}
            </Text>
          ))}
        </div>
      </div>
      {rows.busy.map((v) => renderRow(v.id, v.fleetNumber))}
      {rows.shownIdle.map((v) => renderRow(v.id, v.fleetNumber))}
      {rows.hiddenIdle > 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('scheduleOverview.idleVehiclesHidden', { count: rows.hiddenIdle })}
        </Text>
      ) : null}
    </div>
  );
}

function minutesToHourLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  return `${String(h).padStart(2, '0')}:00`;
}

export interface OverviewUnscheduledListProps {
  jobs: ScheduleOverviewDayDetail['unscheduledJobs'];
  onOpenSchedule: (jobId?: string) => void;
}

export function OverviewUnscheduledList({
  jobs,
  onOpenSchedule,
}: OverviewUnscheduledListProps) {
  const { t } = useTranslation();

  if (jobs.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('scheduleOverview.noUnscheduled')}
      />
    );
  }

  return (
    <List
      size="small"
      dataSource={jobs}
      renderItem={(job) => (
        <List.Item
          actions={[
            <a key="go" onClick={() => onOpenSchedule(job.id)}>
              {t('scheduleOverview.assignInDaily')}
            </a>,
          ]}
        >
          <List.Item.Meta
            title={
              <span>
                {job.title}{' '}
                <Tag style={{ marginInlineStart: 4 }}>{job.jobType}</Tag>
              </span>
            }
            description={
              <Text type="secondary" style={{ fontSize: 12 }}>
                {job.earliestStart
                  ? t('scheduleOverview.jobEarliest', { time: job.earliestStart })
                  : t('scheduleOverview.jobNoTime')}
                {' · '}
                {t('scheduleOverview.jobPax', { count: job.passengerCount })}
              </Text>
            }
          />
        </List.Item>
      )}
    />
  );
}
